"use client"

import { useReadContract, useWriteContract, useWaitForTransactionReceipt, useChainId, useAccount } from "wagmi"
import { parseEther, formatUnits, formatEther } from "viem"
import { useState, useEffect } from "react"
import { TKS_COLLECTOR_ABI, TKS_COLLECTOR_ADDRESSES, ERC20_ABI, STABLECOIN_ADDRESSES } from "./tks-presale-abi"

// Native token address constant
const NATIVE_TOKEN = "0x0000000000000000000000000000000000000000" as const

/**
 * Get the TKS Collector contract address for the current chain
 */
export function useTKSCollectorAddress(): `0x${string}` | undefined {
    const chainId = useChainId()
    return TKS_COLLECTOR_ADDRESSES[chainId]
}

export function useTKSPresaleStats() {
    const collectorAddress = useTKSCollectorAddress()
    const chainId = useChainId()

    const { data, isLoading, error, refetch } = useReadContract({
        address: collectorAddress,
        abi: TKS_COLLECTOR_ABI,
        functionName: "getPresaleStats",
        query: {
            enabled: !!collectorAddress,
            refetchInterval: 10000, // Refresh every 10 seconds
        },
    })

    const [globalStats, setGlobalStats] = useState<{
        totalRaisedUsd: number;
        totalTksSold: number;
        contributorCount: number;
    } | null>(null)

    const fetchGlobalStats = async () => {
        try {
            const resp = await fetch('/api/tks-init')
            const result = await resp.json()
            if (result.success && result.presale.globalStats) {
                setGlobalStats(result.presale.globalStats)
            }
        } catch (e) {
            console.error("Failed to fetch global stats:", e)
        }
    }

    useEffect(() => {
        fetchGlobalStats()
        // Poll global stats Every 10 seconds for real-time global sync
        const interval = setInterval(fetchGlobalStats, 10000)
        return () => clearInterval(interval)
    }, [])

    // Parse the data and merge with global stats
    // globalStats from /api/tks-init is the CROSS-CHAIN combined total (all EVM + non-EVM)
    // data[0] is the per-chain on-chain value for the currently connected chain only
    // For totalRaisedUsd/totalTksSold/contributorCount: always prefer globalStats (combined)
    // Only fall back to per-chain on-chain data when globalStats hasn't loaded yet
    const stats = data ? {
        totalRaisedUsd: globalStats
            ? globalStats.totalRaisedUsd  // Cross-chain combined total (source of truth)
            : Number(formatUnits(data[0], 8)), // Temporary: per-chain until API loads
        totalTksSold: globalStats
            ? globalStats.totalTksSold
            : Number(formatEther(data[1])),
        contributorCount: globalStats
            ? globalStats.contributorCount
            : Number(data[2]),
        tksPerUsd: Number(formatEther(data[3])),
        isPaused: data[4],
    } : (globalStats ? {
        totalRaisedUsd: globalStats.totalRaisedUsd,
        totalTksSold: globalStats.totalTksSold,
        contributorCount: globalStats.contributorCount,
        tksPerUsd: 10,
        isPaused: false,
    } : null)

    return {
        stats,
        isLoading,
        error,
        refetch,
        refetchGlobal: fetchGlobalStats,
        collectorAddress,
        chainId,
    }
}

/**
 * Fetch user-specific TKS presale stats
 */
export function useTKSUserStats() {
    const { address } = useAccount()
    const collectorAddress = useTKSCollectorAddress()

    const { data, isLoading, error, refetch } = useReadContract({
        address: collectorAddress,
        abi: TKS_COLLECTOR_ABI,
        functionName: "getUserStats",
        args: address ? [address] : undefined,
        query: {
            enabled: !!collectorAddress && !!address,
            refetchInterval: 15000, // Refresh every 15 seconds
        },
    })

    const userStats = data ? {
        usdContributed: Number(formatUnits(data[0], 8)),
        tksEarned: Number(formatEther(data[1])),
        referralEarningsUsd: Number(formatUnits(data[2], 8)),
        referralTksEarned: Number(formatEther(data[3])),
        referrer: data[4],
    } : null

    return { userStats, isLoading, error, refetch }
}

/**
 * Fetch real-time native token price from Chainlink
 */
export function useTKSNativePrice() {
    const collectorAddress = useTKSCollectorAddress()

    const { data, isLoading, error, refetch } = useReadContract({
        address: collectorAddress,
        abi: TKS_COLLECTOR_ABI,
        functionName: "getNativePrice",
        query: {
            enabled: !!collectorAddress,
            refetchInterval: 60000, // Refresh every minute
        },
    })

    // Price is in 8 decimals
    const nativePrice = data ? Number(formatUnits(data, 8)) : null

    return { nativePrice, isLoading, error, refetch }
}

/**
 * Calculate TKS tokens for a given payment
 */
export function useCalculateTKS(payToken: `0x${string}`, amount: bigint) {
    const collectorAddress = useTKSCollectorAddress()

    const { data, isLoading, error } = useReadContract({
        address: collectorAddress,
        abi: TKS_COLLECTOR_ABI,
        functionName: "calculateTks",
        args: [payToken, amount],
        query: {
            enabled: !!collectorAddress && amount > BigInt(0),
        },
    })

    const result = data ? {
        tksAmount: Number(formatEther(data[0])),
        usdValue: Number(formatUnits(data[1], 8)),
    } : null

    return { result, isLoading, error }
}

/**
 * Execute native currency contribution
 */
export function useTKSContributeNative() {
    const collectorAddress = useTKSCollectorAddress()
    const { writeContract, data: hash, isPending, error } = useWriteContract()
    const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash })

    const contribute = (amountInEther: string, referrer?: `0x${string}`) => {
        if (!collectorAddress) return

        const value = parseEther(amountInEther)

        if (referrer && referrer !== "0x0000000000000000000000000000000000000000") {
            writeContract({
                address: collectorAddress,
                abi: TKS_COLLECTOR_ABI,
                functionName: "contributeWithReferral",
                args: [referrer],
                value,
            })
        } else {
            writeContract({
                address: collectorAddress,
                abi: TKS_COLLECTOR_ABI,
                functionName: "contribute",
                value,
            })
        }
    }

    return {
        contribute,
        hash,
        isPending,
        isConfirming,
        isSuccess,
        error,
    }
}

/**
 * Execute stablecoin contribution (requires approval first)
 */
export function useTKSContributeToken() {
    const collectorAddress = useTKSCollectorAddress()
    const { writeContract, data: hash, isPending, error } = useWriteContract()
    const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash })

    const contributeToken = (
        tokenAddress: `0x${string}`,
        amount: bigint,
        referrer?: `0x${string}`
    ) => {
        if (!collectorAddress) return

        if (referrer && referrer !== "0x0000000000000000000000000000000000000000") {
            writeContract({
                address: collectorAddress,
                abi: TKS_COLLECTOR_ABI,
                functionName: "contributeWithTokenAndReferral",
                args: [tokenAddress, amount, referrer],
            })
        } else {
            writeContract({
                address: collectorAddress,
                abi: TKS_COLLECTOR_ABI,
                functionName: "contributeWithToken",
                args: [tokenAddress, amount],
            })
        }
    }

    return {
        contributeToken,
        hash,
        isPending,
        isConfirming,
        isSuccess,
        error,
    }
}

/**
 * Approve token spending for the collector contract
 */
export function useTokenApproval(tokenAddress: `0x${string}`) {
    const collectorAddress = useTKSCollectorAddress()
    const { address } = useAccount()
    const { writeContract, data: hash, isPending, error } = useWriteContract()
    const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash })

    // Check current allowance
    const { data: allowance, refetch: refetchAllowance } = useReadContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: address && collectorAddress ? [address, collectorAddress] : undefined,
        query: {
            enabled: !!tokenAddress && !!address && !!collectorAddress && tokenAddress !== NATIVE_TOKEN,
        },
    })

    const approve = (amount: bigint) => {
        if (!collectorAddress || !tokenAddress || tokenAddress === NATIVE_TOKEN) return

        writeContract({
            address: tokenAddress,
            abi: ERC20_ABI,
            functionName: "approve",
            args: [collectorAddress, amount],
        })
    }

    return {
        approve,
        allowance: allowance || BigInt(0),
        refetchAllowance,
        hash,
        isPending,
        isConfirming,
        isSuccess,
        error,
    }
}

/**
 * Get stablecoins available for the current chain
 */
export function useAvailableStablecoins() {
    const chainId = useChainId()
    return STABLECOIN_ADDRESSES[chainId] || []
}

/**
 * Combined hook for complete presale data
 */
export function useTKSPresale() {
    const { stats, isLoading: statsLoading, refetch: refetchStats, refetchGlobal, collectorAddress, chainId } = useTKSPresaleStats()
    const { userStats, isLoading: userLoading, refetch: refetchUser } = useTKSUserStats()
    const { nativePrice, isLoading: priceLoading, refetch: refetchPrice } = useTKSNativePrice()
    const stablecoins = useAvailableStablecoins()

    const refetchAll = () => {
        refetchStats()
        refetchUser()
        refetchPrice()
        refetchGlobal()
    }

    return {
        // Global stats
        stats,
        nativePrice,

        // User stats
        userStats,

        // Chain info
        collectorAddress,
        chainId,
        stablecoins,

        // Loading states
        isLoading: statsLoading || userLoading || priceLoading,

        // Refetch
        refetchAll,
    }
}
